import { useParams, Navigate } from "react-router-dom";
import { Link } from "@/i18n/LocaleLink";
import { useLang } from "@/i18n/LanguageContext";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { NewsletterForm } from "@/components/NewsletterForm";
import { GuideBlock } from "@/components/GuideBlock";
import { THRESHOLD_IMAGES, OFFERING_IMAGES, useContent } from "@/data/content";
import { useSeo } from "@/hooks/useSeo";

export default function ThresholdPage() {
  const { slug } = useParams();
  const { lang } = useLang();
  const { ui, thresholds, offerings } = useContent();
  const threshold = thresholds.find((t) => t.slug === slug);
  useSeo({
    title: threshold ? (threshold.seo?.title || `${threshold.name} | Women's Den`) : "Women's Den",
    description: threshold ? (threshold.seo?.description || threshold.short) : "",
    image: THRESHOLD_IMAGES[slug],
  });
  if (!threshold) return <Navigate to={lang === "nl" ? "/nl" : "/"} replace />;
  const related = offerings.filter((o) => o.threshold === slug);
  const t2 = ui.threshold;

  return (
    <main data-testid={`threshold-page-${slug}`}>
      <PageHero
        eyebrow={threshold.subtitle}
        title={threshold.heroLine}
        image={THRESHOLD_IMAGES[slug]}
        tall
        testid="threshold-hero"
      />

      <section className="bg-cream py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid gap-14 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{threshold.name}</h2>
            {threshold.intro.map((p, i) => (
              <p key={i} className={`mt-6 text-base leading-relaxed text-soft ${i === 0 ? "drop-cap" : ""}`}>{p}</p>
            ))}
            {threshold.denNote && (
              <p className="mt-12 border-l-2 border-clay/40 pl-6 font-serif italic text-xl leading-snug text-ink" data-testid="threshold-den-note">{threshold.denNote}</p>
            )}
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-4 lg:col-start-9">
            <div className="border-l-2 border-clay/40 pl-8">
              <p className="text-xs tracking-[0.3em] uppercase text-soft mb-6" data-testid="threshold-feelings-heading">{threshold.feelingsHeading || t2.feeling}</p>
              <ul className="space-y-4">
                {threshold.feelings.map((f) => (
                  <li key={f} className="font-serif italic text-xl leading-snug text-ink">{f}</li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {threshold.guides && threshold.guides.length > 0 && (
        <GuideBlock ids={threshold.guides} bg="bg-cream" testid={`threshold-guides-${slug}`} />
      )}

      {related.length > 0 && (
        <section className="bg-parchment py-24 lg:py-32" data-testid="threshold-offerings">
          <div className="mx-auto max-w-7xl px-6 lg:px-10">
            <Reveal>
              <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">
                {t2.accompaniedTitle} <span className="italic text-clay">{t2.accompaniedAccent}</span>
              </h2>
            </Reveal>
            <div className="mt-14 grid gap-10 md:grid-cols-2 lg:grid-cols-3">
              {related.map((o, i) => (
                <Reveal key={o.slug} delay={i * 0.1} className="h-full">
                  <Link to={`/offerings/${o.slug}`} data-testid={`offering-card-${o.slug}`} className="group flex h-full flex-col">
                    <div className="overflow-hidden rounded-t-[8rem]">
                      <img loading="lazy" src={OFFERING_IMAGES[o.slug]} alt={o.name} className="aspect-[4/5] w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105" />
                    </div>
                    <p className="mt-5 text-xs tracking-[0.25em] uppercase text-soft">{o.format}</p>
                    <h3 className="mt-2 font-serif text-2xl text-ink group-hover:text-clay transition-colors duration-300">{o.name}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-soft flex-1">{o.blurb}</p>
                    <span className="mt-5 inline-flex items-center gap-2 text-sm text-clay">
                      {ui.common.readMore} <ArrowUpRight size={15} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </span>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="bg-cream py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid gap-14 lg:grid-cols-2 items-center">
          <Reveal>
            <p className="font-serif italic text-2xl sm:text-3xl leading-snug text-ink" data-testid="threshold-closing-q">
              {threshold.closingQ || t2.unsure1}<br />{threshold.closingLine || t2.unsure2}
            </p>
            <Link
              to={threshold.closingTo || `/contact?topic=${encodeURIComponent(threshold.name)}`}
              data-testid="threshold-cta"
              className="group mt-8 inline-flex items-center gap-3 rounded-full bg-clay px-8 py-4 text-sm tracking-wide text-cream transition-colors duration-300 hover:bg-rust"
            >
              {threshold.cta}
              <ArrowRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="text-sm text-soft mb-4">{t2.newsletterLead}</p>
            <NewsletterForm />
          </Reveal>
        </div>
      </section>
    </main>
  );
}
